import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import {
    Sparkles, Check, Printer, Plus, X, Trash2, Loader2, RefreshCw,
    ChefHat, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import i18n from "@/i18n";
import { tFi, fiDate, fiStatus } from "@/lib/printFi";

const STATUS_LABEL = {
    draft: () => i18n.t("prep.status.draft", "Draft"),
    approved: () => i18n.t("prep.status.approved", "Approved"),
    modified_after_approval: () => i18n.t("prep.status.modified_after_approval", "Modified after approval"),
    missing: () => i18n.t("prep.status.missing", "Not created"),
};
const STATUS_TONE = {
    draft: "pending",
    approved: "completed",
    modified_after_approval: "action_required",
    missing: "low",
};

const uid = () => Math.random().toString(36).slice(2, 10);
const cleanTasks = (tasks) => (tasks || [])
    .filter((t) => (t.label || "").trim().length > 0)
    .map((t) => ({ id: t.id || uid(), label: t.label, done: !!t.done }));

/**
 * Daily Prep List — full editor + AI generation + print, scoped to a
 * single date. Rendered as a modal from WeeklyMenu.jsx.
 */
export default function DailyPrepList({ day, weeklyMenuId, onClose }) {
    const { user, restaurantId } = useApp();
    const { scopedParams: params } = useRestaurantScope();
    const { t } = useTranslation();

    const [doc, setDoc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);   // regenerate preview panel
    const [printOpen, setPrintOpen] = useState(false);
    // Scaled ingredient overlay (UI-only). Maps recipe_id -> {base_yield,
    // ingredients:[{name, quantity, unit}]}. Populated lazily from
    // /recipes/{id} — no new backend logic. Same scaling formula used by
    // Purchase Needs: scaled_qty = base_qty * planned_pax / base_yield.
    const [recipeCache, setRecipeCache] = useState({});

    // Prefetch the recipes for every dish planned on this day so we can
    // render scaled ingredient quantities per service. Failure is silent
    // (feature is additive — never blocks the prep list).
    const recipeKey = (day.dishes || []).map((d) => d.recipe_id).join(",");
    useEffect(() => {
        const ids = Array.from(new Set(recipeKey.split(",").filter(Boolean)));
        if (ids.length === 0) return;
        let alive = true;
        (async () => {
            const results = await Promise.all(ids.map((id) =>
                api.get(`/recipes/${id}`).then((r) => [id, r.data])
                    .catch(() => [id, null])));
            if (!alive) return;
            setRecipeCache((prev) => {
                const next = { ...prev };
                for (const [id, data] of results) {
                    if (!data) continue;
                    next[id] = {
                        base_yield: Number(data.base_yield_quantity) || 1,
                        ingredients: (data.ingredients || []).map((i) => ({
                            name: i.ingredient_name || i.name || "",
                            quantity: Number(i.quantity) || 0,
                            unit: i.unit || "",
                        })),
                    };
                }
                return next;
            });
        })();
        return () => { alive = false; };
    }, [day.date, recipeKey]);

    // Compute the scaled ingredient list for a given prep dish (matched by
    // dish_name against the weekly-menu day's dishes). Returns null when
    // we cannot correlate a recipe / pax — the caller then simply omits
    // the summary and leaves the tasks untouched.
    const scaledIngredientsFor = (dishName) => {
        const dm = (day.dishes || []).find(
            (d) => (d.dish_name || "").trim().toLowerCase()
                    === (dishName || "").trim().toLowerCase());
        if (!dm || !dm.recipe_id) return null;
        const pax = Number(dm.planned_pax) || 0;
        if (pax <= 0) return null;
        const rc = recipeCache[dm.recipe_id];
        if (!rc || rc.ingredients.length === 0) return null;
        const rows = rc.ingredients
            .filter((ing) => (ing.name || "").trim())
            .map((ing) => {
                const scaled = ing.quantity * pax / (rc.base_yield || 1);
                return { name: ing.name, unit: ing.unit,
                          quantity: scaled };
            });
        return { pax, rows };
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/weekly-menu/daily-prep", {
                params: { ...params, date: day.date },
            });
            setDoc(data);
        } catch (e) {
            if (e.response?.status === 404) {
                setDoc(null);
            } else {
                toast.error(apiErrorMessage(e));
            }
        } finally { setLoading(false); }
    }, [day.date, params]);
    useEffect(() => { load(); }, [load]);

    const runGenerate = async () => {
        setGenerating(true);
        try {
            const { data } = await api.post("/weekly-menu/daily-prep/generate", {
                ...params, date: day.date, weekly_menu_id: weeklyMenuId,
            });
            if (doc) {
                setPreview(data);
            } else {
                // First generation: seed the document directly (still draft, not saved).
                setDoc({
                    ...data, status: "draft", notes: "", id: null,
                    restaurant_id: data.restaurant_id, weekly_menu_id: weeklyMenuId,
                });
                toast.success(t("prep.draftGenerated", "Draft generated. Review and save."));
            }
        } catch (e) {
            toast.error(apiErrorMessage(e)
                          || t("prep.generationFailed", "Generation failed"));
        } finally { setGenerating(false); }
    };

    const save = async (patch = {}) => {
        if (!doc) return;
        setSaving(true);
        try {
            const payload = {
                ...params,
                date: day.date, weekly_menu_id: weeklyMenuId,
                notes: doc.notes || "",
                dishes: (doc.dishes || []).map((d) => ({
                    ...d,
                    groups: (d.groups || []).map((g) => ({
                        name: g.name,
                        tasks: cleanTasks(g.tasks),
                    })),
                })),
                ...patch,
            };
            const { data } = await api.post("/weekly-menu/daily-prep", payload);
            setDoc(data);
            return data;
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setSaving(false); }
    };

    const approve = async () => {
        const saved = await save();
        if (!saved?.id) return;
        try {
            const { data } = await api.post(
                `/weekly-menu/daily-prep/${saved.id}/approve`);
            setDoc(data);
            toast.success(t("prep.approvedToast", "Daily prep list approved"));
        } catch (e) {
            toast.error(apiErrorMessage(e));
        }
    };

    // ─── dish/group/task mutators ───
    const patchDish = (idx, patch) => {
        setDoc((d) => {
            const dishes = [...(d.dishes || [])];
            dishes[idx] = { ...dishes[idx], ...patch };
            return { ...d, dishes };
        });
    };
    const patchGroup = (di, gi, patch) => {
        setDoc((d) => {
            const dishes = [...d.dishes];
            const groups = [...dishes[di].groups];
            groups[gi] = { ...groups[gi], ...patch };
            dishes[di] = { ...dishes[di], groups };
            return { ...d, dishes };
        });
    };
    const addGroup = (di) => patchDish(di, {
        groups: [...(doc.dishes[di].groups || []),
                   { name: t("prep.newGroup", "New group"), tasks: [] }],
    });
    const removeGroup = (di, gi) => patchDish(di, {
        groups: doc.dishes[di].groups.filter((_, i) => i !== gi),
    });
    const addTask = (di, gi) => patchGroup(di, gi, {
        tasks: [...(doc.dishes[di].groups[gi].tasks || []),
                  { id: uid(), label: "", done: false }],
    });
    const patchTask = (di, gi, ti, patch) => {
        const tasks = [...doc.dishes[di].groups[gi].tasks];
        tasks[ti] = { ...tasks[ti], ...patch };
        patchGroup(di, gi, { tasks });
    };
    const removeTask = (di, gi, ti) => {
        patchGroup(di, gi, {
            tasks: doc.dishes[di].groups[gi].tasks.filter((_, i) => i !== ti),
        });
    };

    // ─── regenerate preview actions ───
    const applyReplace = () => { setDoc((d) => ({ ...d, dishes: preview.dishes })); setPreview(null); };
    const applyMerge = () => {
        setDoc((d) => {
            const nextDishes = (d.dishes || []).map((currentDish) => {
                const suggestion = preview.dishes.find(
                    (p) => p.dish_name === currentDish.dish_name);
                if (!suggestion) return currentDish;
                return {
                    ...currentDish,
                    groups: [...(currentDish.groups || []), ...(suggestion.groups || [])],
                };
            });
            // Also add any dishes present only in the AI suggestion
            const currentNames = new Set(nextDishes.map((x) => x.dish_name));
            const extras = preview.dishes.filter((p) => !currentNames.has(p.dish_name));
            return { ...d, dishes: [...nextDishes, ...extras] };
        });
        setPreview(null);
    };

    const status = doc?.status || (doc ? "draft" : "missing");

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto"
                             data-testid="daily-prep-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ChefHat className="h-5 w-5" />
                        {t("prep.dialogTitle", { defaultValue: "Daily Prep List — {{day}}, {{date}}", day: t(`days.${(day.day_name || "").toLowerCase()}`, day.day_name || ""), date: day.date })}
                    </DialogTitle>
                    <DialogDescription>
                        {t("prep.dialogDescription", "AI generates the first draft. You review, edit and approve. MiseOne never overwrites your changes.")}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-wrap items-center gap-2 py-3 border-b border-neutral-200">
                    <StatusBadge status={STATUS_TONE[status]} data-testid="prep-status-badge">
                        {STATUS_LABEL[status]()}
                    </StatusBadge>
                    {doc?.approved_by && (
                        <span className="text-xs text-neutral-500">
                            {t("prep.approvedBy", { defaultValue: "by {{name}}", name: doc.approved_by })} · {(doc.approved_at || "").slice(0, 16).replace("T", " ")}
                        </span>
                    )}
                    <div className="ml-auto flex flex-wrap gap-2">
                        <button
                            data-testid={doc ? "prep-regenerate-btn" : "prep-generate-btn"}
                            onClick={runGenerate}
                            disabled={generating}
                            className="h-9 px-3 rounded-md bg-neutral-900 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                            {doc ? t("prep.regenerate", "Regenerate") : t("prep.generate", "Generate Daily Prep List")}
                        </button>
                        {doc && (
                            <>
                                <button
                                    data-testid="prep-save-btn"
                                    onClick={() => save()}
                                    disabled={saving}
                                    className="h-9 px-3 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-50"
                                >{t("prep.save", "Save")}</button>
                                <button
                                    data-testid="prep-approve-btn"
                                    onClick={approve}
                                    disabled={saving}
                                    className="h-9 px-3 rounded-md border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 inline-flex items-center gap-1"
                                ><Check className="h-4 w-4" /> {t("prep.approve", "Approve")}</button>
                                <button
                                    data-testid="prep-print-btn"
                                    onClick={() => setPrintOpen(true)}
                                    className="h-9 px-3 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-neutral-50 inline-flex items-center gap-1"
                                ><Printer className="h-4 w-4" /> {t("prep.print", "Print")}</button>
                            </>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div className="py-10 text-center text-sm text-neutral-500">{t("common.loading", "Loading…")}</div>
                ) : !doc ? (
                    <div className="py-10 text-center text-sm text-neutral-500"
                         data-testid="prep-empty-state">
                        {t("prep.emptyBefore", "No prep list yet. Click")} <em>{t("prep.generate", "Generate Daily Prep List")}</em> {t("prep.emptyAfter", "to draft one from the menu.")}
                    </div>
                ) : (
                    <>
                        {preview && (
                            <RegeneratePreview
                                current={doc} preview={preview}
                                onCancel={() => setPreview(null)}
                                onReplace={applyReplace}
                                onMerge={applyMerge} />
                        )}
                        <div className="space-y-6 py-4">
                            {(doc.dishes || []).map((dish, di) => (
                                <DishBlock
                                    key={di} dish={dish} dishIndex={di}
                                    patchDish={(p) => patchDish(di, p)}
                                    patchGroup={patchGroup}
                                    addGroup={() => addGroup(di)}
                                    removeGroup={removeGroup}
                                    addTask={addTask}
                                    patchTask={patchTask}
                                    removeTask={removeTask}
                                    scaled={scaledIngredientsFor(dish.dish_name)} />
                            ))}
                        </div>
                        <div className="pt-4 border-t border-neutral-200">
                            <label className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">
                                {t("prep.kitchenNotes", "Kitchen notes")}
                            </label>
                            <textarea
                                data-testid="prep-notes"
                                value={doc.notes || ""}
                                onChange={(e) => setDoc((d) => ({ ...d, notes: e.target.value }))}
                                rows={3}
                                placeholder={t("prep.notesPlaceholder", "Anything else the brigade should know today?")}
                                className="mt-1 w-full px-3 py-2 rounded-md border border-neutral-300 text-sm"
                            />
                        </div>
                    </>
                )}

                {printOpen && doc && (
                    <PrintDialog
                        startDate={day.date}
                        params={params}
                        onClose={() => setPrintOpen(false)} />
                )}
            </DialogContent>
        </Dialog>
    );
}

// Pretty-print a scaled quantity, upgrading grams → kg / millilitres → l
// when the number gets big, so chefs read "1.5 kg" instead of "1500 g".
function _formatScaled(qty, unit) {
    const n = Number(qty);
    if (!Number.isFinite(n)) return "";
    const u = String(unit || "").trim().toLowerCase();
    // Round to sensible precision.
    const round = (v, d) => {
        const p = Math.pow(10, d);
        return Math.round(v * p) / p;
    };
    if (u === "g" && n >= 1000) return `${round(n / 1000, 2)} kg total`;
    if (u === "ml" && n >= 1000) return `${round(n / 1000, 2)} l total`;
    if (u === "g" || u === "kg") return `${round(n, u === "kg" ? 3 : 0)} ${u} total`;
    if (u === "ml" || u === "l" || u === "cl" || u === "dl")
        return `${round(n, 2)} ${u} total`;
    if (!u) return `${round(n, 2)} total`;
    return `${round(n, 2)} ${u} total`;
}


// Read-only overlay that shows the scaled ingredient totals for a dish
// given the day's planned pax. Reuses the same scaling formula
// (base_qty * pax / base_yield) that Purchase Needs already uses. When
// scaled is null the badge is omitted silently — never blocks the list.
function ScaledIngredientsBadge({ scaled, dishIndex }) {
    const { t } = useTranslation();
    if (!scaled || !scaled.rows || scaled.rows.length === 0) return null;
    return (
        <div
            data-testid={`prep-dish-scaled-${dishIndex}`}
            className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2"
        >
            <div className="text-[11px] uppercase tracking-wide font-semibold text-emerald-800 mb-1">
                {t("prep.ingredientTotals", { defaultValue: "Ingredient totals · {{pax}} pax", pax: scaled.pax })}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5">
                {scaled.rows.map((r, i) => (
                    <li key={i} className="text-xs text-neutral-800 flex items-baseline gap-1.5">
                        <span className="font-medium truncate">{r.name}</span>
                        <span className="text-neutral-400">→</span>
                        <span className="tabular-nums font-semibold text-emerald-900">
                            {_formatScaled(r.quantity, r.unit)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}


function DishBlock({ dish, dishIndex, patchDish, patchGroup, addGroup,
                       removeGroup, addTask, patchTask, removeTask, scaled }) {
    const { t } = useTranslation();
    return (
        <div className="rounded-lg border border-neutral-200 overflow-hidden"
             data-testid={`prep-dish-${dishIndex}`}>
            <header className="bg-neutral-50 px-4 py-3 border-b border-neutral-200">
                <input
                    value={dish.dish_name}
                    onChange={(e) => patchDish({ dish_name: e.target.value })}
                    className="w-full font-semibold text-base bg-transparent focus:outline-none"
                />
                {dish.dish_description && (
                    <div className="text-xs text-neutral-500 mt-1">{dish.dish_description}</div>
                )}
                <ScaledIngredientsBadge scaled={scaled} dishIndex={dishIndex} />
            </header>
            <div className="p-4 space-y-4">
                {(dish.groups || []).map((g, gi) => (
                    <div key={gi} className="rounded-md bg-white border border-neutral-200 p-3"
                         data-testid={`prep-group-${dishIndex}-${gi}`}>
                        <div className="flex items-center gap-2 mb-2">
                            <input
                                data-testid={`prep-group-name-${dishIndex}-${gi}`}
                                value={g.name}
                                onChange={(e) => patchGroup(dishIndex, gi,
                                                              { name: e.target.value })}
                                className="flex-1 font-semibold text-sm bg-transparent focus:outline-none border-b border-transparent hover:border-neutral-200 focus:border-neutral-400"
                            />
                            <button
                                data-testid={`prep-group-remove-${dishIndex}-${gi}`}
                                onClick={() => removeGroup(dishIndex, gi)}
                                className="h-7 w-7 grid place-items-center rounded hover:bg-rose-50"
                                title={t("prep.deleteGroup", "Delete group")}
                            ><Trash2 className="h-3.5 w-3.5 text-rose-500" /></button>
                        </div>
                        <ul className="space-y-1">
                            {(g.tasks || []).map((tk, ti) => (
                                <li key={tk.id || ti} className="flex items-center gap-2">
                                    <input
                                        type="checkbox" checked={!!tk.done}
                                        onChange={(e) => patchTask(dishIndex, gi, ti,
                                                                     { done: e.target.checked })}
                                        className="h-4 w-4"
                                        data-testid={`prep-task-check-${dishIndex}-${gi}-${ti}`}
                                    />
                                    <input
                                        data-testid={`prep-task-label-${dishIndex}-${gi}-${ti}`}
                                        value={tk.label}
                                        onChange={(e) => patchTask(dishIndex, gi, ti,
                                                                     { label: e.target.value })}
                                        placeholder={t("prep.taskPlaceholder", "Task…")}
                                        className={`flex-1 text-sm bg-transparent focus:outline-none border-b border-transparent hover:border-neutral-200 focus:border-neutral-400 ${
                                            tk.done ? "line-through text-neutral-400" : ""}`}
                                    />
                                    <button
                                        onClick={() => removeTask(dishIndex, gi, ti)}
                                        className="h-6 w-6 grid place-items-center rounded hover:bg-neutral-100"
                                        title={t("prep.deleteTask", "Delete task")}
                                    ><X className="h-3 w-3 text-neutral-400" /></button>
                                </li>
                            ))}
                            <li>
                                <button
                                    data-testid={`prep-task-add-${dishIndex}-${gi}`}
                                    onClick={() => addTask(dishIndex, gi)}
                                    className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1"
                                ><Plus className="h-3 w-3" /> {t("prep.addTask", "Add task")}</button>
                            </li>
                        </ul>
                    </div>
                ))}
                <button
                    data-testid={`prep-group-add-${dishIndex}`}
                    onClick={addGroup}
                    className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 inline-flex items-center gap-1"
                ><Plus className="h-3 w-3" /> {t("prep.addGroup", "Add group")}</button>
            </div>
        </div>
    );
}

function RegeneratePreview({ current, preview, onCancel, onReplace, onMerge }) {
    const { t } = useTranslation();
    return (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 my-4"
             data-testid="prep-regenerate-preview">
            <div className="flex items-start gap-3">
                <RefreshCw className="h-5 w-5 text-amber-700 mt-0.5" />
                <div className="flex-1">
                    <div className="font-semibold text-amber-900">{t("prep.newProposal", "New AI proposal ready")}</div>
                    <p className="text-sm text-amber-800 mt-1">
                        {t("prep.chooseHow", "Choose how to apply it. Nothing overwrites automatically.")}
                    </p>
                    <div className="mt-3 grid md:grid-cols-2 gap-3">
                        <PreviewColumn title={t("prep.current", "Current")} data={current.dishes} />
                        <PreviewColumn title={t("prep.aiSuggestion", "AI suggestion")} data={preview.dishes} />
                    </div>
                    <div className="mt-3 flex gap-2">
                        <button
                            data-testid="prep-regenerate-replace"
                            onClick={onReplace}
                            className="h-9 px-3 rounded-md bg-neutral-900 text-white text-sm font-semibold"
                        >{t("prep.replaceWithAi", "Replace with AI")}</button>
                        <button
                            data-testid="prep-regenerate-merge"
                            onClick={onMerge}
                            className="h-9 px-3 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-white"
                        >{t("prep.merge", "Merge (append AI groups)")}</button>
                        <button
                            data-testid="prep-regenerate-cancel"
                            onClick={onCancel}
                            className="h-9 px-3 rounded-md border border-neutral-300 text-sm font-semibold hover:bg-white"
                        >{t("prep.keepCurrent", "Keep current")}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PreviewColumn({ title, data }) {
    return (
        <div className="rounded-md bg-white border border-neutral-200 p-3 max-h-64 overflow-y-auto">
            <div className="text-xs font-semibold uppercase tracking-wide text-neutral-500 mb-2">
                {title}
            </div>
            {(data || []).map((d, i) => (
                <div key={i} className="mb-2">
                    <div className="text-sm font-semibold">{d.dish_name}</div>
                    {(d.groups || []).map((g, gi) => (
                        <div key={gi} className="text-xs text-neutral-600 mt-1">
                            <strong>{g.name}:</strong> {(g.tasks || []).map((t) =>
                                typeof t === "string" ? t : t.label).join(", ")}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function PrintDialog({ startDate, params, onClose }) {
    const { t } = useTranslation();
    const [range, setRange] = useState("day");
    const [bundle, setBundle] = useState(null);
    const [busy, setBusy] = useState(false);

    const fetchBundle = useCallback(async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/weekly-menu/daily-prep/print", {
                params: { ...params, start_date: startDate, range_type: range },
            });
            setBundle(data);
        } catch (e) {
            toast.error(apiErrorMessage(e));
        } finally { setBusy(false); }
    }, [range, params, startDate]);
    useEffect(() => { fetchBundle(); }, [fetchBundle]);

    const doPrint = () => {
        // Trigger the browser's native print dialog on the print-view iframe
        setTimeout(() => window.print(), 100);
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"
                             data-testid="prep-print-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 print:hidden">
                        <FileText className="h-5 w-5" /> {t("prep.printTitle", "Print Daily Prep List")}
                    </DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-2 print:hidden py-3 border-b border-neutral-200">
                    {[
                        { v: "day",      label: t("prep.range.day", "Today") },
                        { v: "two_days", label: t("prep.range.two_days", "2 days") },
                        { v: "week",     label: t("prep.range.week", "This week") },
                    ].map((r) => (
                        <button
                            key={r.v}
                            data-testid={`prep-print-range-${r.v}`}
                            onClick={() => setRange(r.v)}
                            className={`h-9 px-3 rounded-md border text-sm font-semibold ${
                                range === r.v ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-300 hover:bg-neutral-50"
                            }`}>{r.label}</button>
                    ))}
                    <button
                        data-testid="prep-print-do"
                        onClick={doPrint}
                        disabled={busy}
                        className="ml-auto h-9 px-4 rounded-md bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
                    ><Printer className="h-4 w-4" /> {t("prep.print", "Print")}</button>
                </div>

                {busy ? (
                    <div className="py-10 text-center text-sm text-neutral-500">{t("common.loading", "Loading…")}</div>
                ) : (
                    <PrintablePage bundle={bundle} />
                )}
            </DialogContent>
        </Dialog>
    );
}

function PrintablePage({ bundle }) {
    if (!bundle) return null;
    return (
        <div className="p-8 bg-white text-neutral-900 print:p-0" data-testid="prep-printable">
            <header className="mb-6 border-b-2 border-neutral-900 pb-3">
                <div className="text-xs uppercase tracking-[0.32em] text-neutral-600">MiseOne</div>
                <div className="font-display text-4xl font-extrabold">{tFi("ui.printMenuDialog.dailyPrepList")}</div>
            </header>
            {(bundle.days || []).map((day) => (
                <section key={day.date} className="mb-8 break-inside-avoid">
                    <div className="flex items-baseline justify-between border-b border-neutral-300 mb-3 pb-1">
                        <h2 className="font-display text-2xl font-bold">
                            {fiDate(day.date, { weekday: "long", day: "numeric", month: "numeric", year: "numeric" })}
                        </h2>
                        <span className="text-xs uppercase tracking-wide text-neutral-500">
                            {fiStatus(day.status || "draft")}
                        </span>
                    </div>
                    {(!day.dishes || day.dishes.length === 0) ? (
                        <p className="text-sm text-neutral-500 italic">{tFi("ui.printMenuDialog.noPrepListForThisDay")}</p>
                    ) : day.dishes.map((dish, di) => (
                        <div key={di} className="mb-5">
                            <div className="font-semibold text-lg">{dish.dish_name}</div>
                            {(dish.groups || []).map((g, gi) => (
                                <div key={gi} className="mt-2 ml-4">
                                    <div className="text-sm font-semibold uppercase tracking-wide text-neutral-700">
                                        {g.name}
                                    </div>
                                    <ul className="mt-1">
                                        {(g.tasks || []).map((t, ti) => (
                                            <li key={ti} className="text-sm ml-4 flex items-start gap-2">
                                                <span className="inline-block h-3.5 w-3.5 border border-neutral-500 mt-0.5"></span>
                                                <span>{typeof t === "string" ? t : t.label}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    ))}
                    {day.notes && (
                        <div className="text-sm mt-2 italic text-neutral-700">
                            <strong>{tFi("common.notes")}:</strong> {day.notes}
                        </div>
                    )}
                </section>
            ))}
            <section className="mt-10 break-inside-avoid">
                <div className="text-sm font-semibold uppercase tracking-wide">{tFi("ui.printMenuDialog.kitchenNotes")}</div>
                <div className="mt-2 space-y-4">
                    {[1, 2, 3].map((n) => (
                        <div key={n} className="border-b border-neutral-400 h-6"></div>
                    ))}
                </div>
            </section>
        </div>
    );
}

// Print CSS — only when the print dialog is open, hide everything else.
// (Loaded via a style tag to keep this component self-contained.)
if (typeof document !== "undefined" && !document.getElementById("prep-print-css")) {
    const style = document.createElement("style");
    style.id = "prep-print-css";
    style.textContent = `
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="prep-print-dialog"], [data-testid="prep-print-dialog"] * {
            visibility: visible !important;
          }
          [data-testid="prep-print-dialog"] {
            position: absolute !important;
            left: 0; top: 0; width: 100%;
            max-width: none !important; max-height: none !important;
            box-shadow: none !important; border: 0 !important;
            padding: 0 !important;
          }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          @page { size: A4; margin: 16mm; }
        }
    `;
    document.head.appendChild(style);
}
